import {
  Component,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  HostListener,
} from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../services/language.service';
import { AuthService } from '../services/auth.service';
import { CompanyService, ApplicationData } from '../services/company.service';
import {
  catchError,
  filter,
  of,
  startWith,
  Subject,
  switchMap,
  takeUntil,
  combineLatest,
  map,
} from 'rxjs';
import { KeycloakProfile } from 'keycloak-js';
import { set } from 'lodash';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, TranslateModule, RouterModule],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {
  currentLang: string = 'en';
  isRtl: boolean = false;
  isAuthenticated: boolean = false;
  isLandingPage: boolean = false;
  userFullName: string = 'User';
  userEmail: string = '';
  userInitials: string = 'U';
  userAvatarColor: string = '#6B7280';
  showUserDropdown: boolean = false;
  isSidenavOpen: boolean = true;
  authChecked: boolean = false;
  profileLoading: boolean = false;
  currentRoute: string = '';
  showBackToDashboard: boolean = false;
  progress: number = 0;
  showProgressBar: boolean = false;

  @Output() toggleSidenav = new EventEmitter<void>();
  private destroy$ = new Subject<void>();
  private userProfile: KeycloakProfile | null = null;
  currentSubtitle: any;
  private readonly landingRoutes = new Set(['/', '/about', '/contact', '/faq']);
  private lastLandingStateKey: string | null = null;

  constructor(
    private router: Router,
    private languageService: LanguageService,
    private authService: AuthService,
    private activatedRoute: ActivatedRoute,
    private translateService: TranslateService,
    private companyService: CompanyService
  ) {
    this.currentLang = this.languageService.getCurrentLang();
    this.isRtl = this.languageService.isRtl();

    // Set isLandingPage immediately based on current URL to avoid flickering
    const currentPath = window.location.pathname;
    this.isLandingPage = this.landingRoutes.has(currentPath);
  }

  ngOnInit(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => {
          let route = this.activatedRoute;
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        map(
          (route) => route.snapshot.data['title'] || this.getRouteNameFromUrl()
        )
      )
      .subscribe((title: string) => {
        console.log('Header: Route title changed to:', title);
        if (!['about', 'contact', 'faq'].includes(title.toLowerCase().trim())) {
          this.currentRoute = title;
        }
      });
    set;
    this.checkShowBackButton();
    this.updateLandingPageState('init');

    // Listen to route changes
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.checkShowBackButton();
        this.updateLandingPageState('navigation');
      });

    // Set initial route - check route data first, then fallback to URL
    this.setInitialRoute();
    this.setupLanguageSubscription();
    this.setupAuthSubscription();
    this.setupOutsideClickHandler();
    this.setupProgressSubscription();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    document.removeEventListener('click', this.handleOutsideClick);
  }

  private checkShowBackButton(): void {
    const currentUrl = this.router.url;

    const routesWithBackButton = [
      '/company-details',
      '/import-employees',
      '/employee-details',
      '/health-declaration',
      '/quotations',
      '/payment',
    ];
    console.log(
      'Header: Checking back button visibility for URL:',
      currentUrl,
      this.isAuthenticated
    );
    this.showBackToDashboard =
      !currentUrl.includes('/dashboard') &&
      routesWithBackButton.some((route) => currentUrl.includes(route));

    this.showProgressBar = this.showBackToDashboard;
  }

  /**
   * Navigate back to dashboard
   */
  navigateBackToDashboard(): void {
    this.router.navigate(['/dashboard']);
  }

  private setInitialRoute(): void {
    let route = this.activatedRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }
    // Prioritize route data title (translation key) over URL-based title
    const routeTitle =
      route.snapshot.data['title'] || this.getRouteNameFromUrl();
    const subtitle = route.snapshot.data['subtitle'];
    console.log('Header: Initial route title set to:', routeTitle);
    if (!['about', 'contact', 'faq'].includes(routeTitle.toLowerCase().trim())) {
      this.currentRoute = routeTitle;
    }
    this.currentSubtitle = subtitle;
  }

  private getRouteNameFromUrl(): string {
    const url = this.router.url;
    const segments = url.split('/').filter((segment) => segment);
    return segments.length > 0
      ? this.formatRouteName(segments[segments.length - 1])
      : '';
  }

  private formatRouteName(route: string): string {
    // Remove query parameters
    route = route.split('?')[0];
    // Convert kebab-case to Title Case
    return route
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  private setupLanguageSubscription(): void {
    this.languageService.currentLang$
      .pipe(takeUntil(this.destroy$))
      .subscribe((lang) => {
        console.log('Header: Language changed to:', lang);
        this.currentLang = lang;
        this.isRtl = lang === 'ar';
      });
  }

  private setupAuthSubscription(): void {
    combineLatest([
      this.authService.isInitialized$,
      this.authService.isAuthenticated$,
      this.authService.profileLoading$,
    ])
      .pipe(
        filter(([initialized]) => initialized),
        switchMap(([_, authenticated, loading]) => {
          this.authChecked = true;
          this.profileLoading = loading;
          this.isAuthenticated = authenticated;

          if (!authenticated) {
            this.clearUserData();
            return of(null);
          }

          return this.authService.userProfile$.pipe(
            startWith(this.authService.getCachedUserProfile()),
            catchError(() => {
              console.warn('Error loading profile, using cached data');
              return of(this.authService.getCachedUserProfile());
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((profile) => {
        this.handleProfileUpdate(profile);
        this.updateLandingPageState('auth-change');
      });
  }

  private setupOutsideClickHandler(): void {
    document.addEventListener('click', this.handleOutsideClick.bind(this));
  }

  private setupProgressSubscription(): void {
    this.companyService.applicationData$
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: ApplicationData) => {
        this.progress = this.companyService.getProgress();
      });
  }

  private handleProfileUpdate(profile: KeycloakProfile | null): void {
    this.userProfile = profile;
    if (profile) {
      this.userEmail = profile.email || '';
      this.userFullName =
        [profile.firstName, profile.lastName].filter(Boolean).join(' ') ||
        'User';
      this.setUserAvatar();
    } else {
      this.clearUserData();
    }
  }

  private clearUserData(): void {
    this.userProfile = null;
    this.userFullName = 'User';
    this.userEmail = '';
    this.userInitials = 'U';
    this.userAvatarColor = '#6B7280';
  }

  private setUserAvatar(): void {
    if (this.userFullName && this.userFullName !== 'User') {
      this.userInitials = this.userFullName
        .split(' ')
        .map((name) => name[0])
        .join('')
        .toUpperCase()
        .substring(0, 2);
      this.userAvatarColor = this.generateColorFromName(this.userFullName);
    }
  }

  private generateColorFromName(name: string): string {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = Math.abs(hash % 360);
    return `hsl(${hue}, 70%, 50%)`;
  }

  @HostListener('document:click', ['$event'])
  handleOutsideClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const dropdownContainer = target.closest('.user-dropdown-container');
    const menuButton = target.closest('[aria-label="Toggle menu"]');

    if (!dropdownContainer && !menuButton && this.showUserDropdown) {
      this.showUserDropdown = false;
    }
  }

  get isDashboard(): boolean {
    return this.router.url.startsWith('/dashboard');
  }

  private updateLandingPageState(trigger: 'init' | 'navigation' | 'auth-change'): void {
    const fullUrl = this.router.url;
    const urlPath = fullUrl.split('?')[0];
    const isLanding = this.landingRoutes.has(urlPath);
    const shouldShowButtons = !this.isAuthenticated && isLanding;
    const stateKey = `${fullUrl}|${this.isAuthenticated}`;

    if (this.lastLandingStateKey !== stateKey) {
      console.log('[Header] isLandingPage state updated:', {
        trigger,
        fullUrl,
        urlPath,
        isLanding,
        isAuthenticated: this.isAuthenticated,
        shouldShowButtons,
      });
      this.lastLandingStateKey = stateKey;
    }

    this.isLandingPage = isLanding;
  }

  onLogoClick(): void {
    this.router.navigate(['/']);
  }

  switchLanguage(lang: string): void {
    this.languageService.setLanguage(lang);
  }

  onToggleSidenav(): void {
    this.isSidenavOpen = !this.isSidenavOpen;
    this.toggleSidenav.emit();
  }

  openKeyClockLogin(): void {
    this.authService.login();
  }

  navigateToDashboard(): void {
    this.router.navigate(['/dashboard/home']);
  }

  logout(): void {
    this.authService.logout();
  }

  toggleUserDropdown(event: Event): void {
    event.stopPropagation();
    this.showUserDropdown = !this.showUserDropdown;
  }

  goToProfileSettings(): void {
    this.showUserDropdown = false;
    this.router.navigate(['/dashboard/settings']);
  }
}
